import {
	ContractBondSubtype,
	FileType,
	isCommercialSuretyBondType,
	isContractCategoryBondType,
	type SuretyBondFormType,
} from "@inrev/common";
import {
	buildCommercialBondTypeOptions,
	LEGACY_BOND_TYPE_OPTIONS,
} from "@portal/components/features/bond-request-submission/bondRequestBondTypeOptions";
import { resolveContractSuretyType } from "@portal/components/features/bond-request-submission/bondRequestSubmissionBondTypeUtils";
import { FormItemLabel } from "@portal/components/form";
import {
	BOND_FORM_HEADER,
	BOND_FORM_UPLOAD_ONLY_TOOLTIP,
	BondFormSectionHeader,
} from "@portal/components/form/BondFormSectionHeader";
import {
	FormCurrencyComponent,
	FormCurrencyPercentComponent,
	FormDateComponent,
	FormDropdownComponent,
	FormInputComponent,
} from "@portal/components/form/fieldLayouts";
import { FormBondFormSelect } from "@portal/components/form/fields/FormBondFormSelect";
import { FileUpload } from "@portal/components/ui/Upload";
import { useIsCommercialSuretyEnabled } from "@portal/hooks/useIsCommercialSuretyEnabled";
import { useIsMinimalBondUIEnabled } from "@portal/hooks/useIsMinimalBondUIEnabled";
import { useEffect, useMemo, useRef } from "react";
import type { Path } from "react-hook-form";
import { useController, useFormContext } from "react-hook-form";
import { EMPTY_BOND_FORM, PROJECT_NAME_TOOLTIP } from "../../../../../../constants";
import type { AdminCombinedFormData } from "../../schema";
import { QuickSubmitFormItemGroup } from "../QuickSubmitFormItemGroup";

const quoteFieldsToClear: Path<AdminCombinedFormData>[] = [
	"quote.bondAmount",
	"quote.bidAmount",
	"quote.bondAmountPercentOfBid",
	"quote.bidBondAmountType",
	"quote.contractAmount",
	"quote.project.description",
];

const EMPTY_TEMPLATES: never[] = [];
const NO_TYPE_LABELS: Partial<Record<SuretyBondFormType, string>> = {};

const quoteResetKey = (
	isCommercialSuretyEnabled: boolean,
	bondType: AdminCombinedFormData["bondType"],
	contractBondType: AdminCombinedFormData["contractBondType"],
	contractSuretyType: AdminCombinedFormData["quote"]["contractSuretyType"],
) => {
	if (!isCommercialSuretyEnabled) return contractSuretyType;
	if (bondType == null) return undefined;
	if (isCommercialSuretyBondType(bondType)) return bondType;
	return `${bondType}:${contractBondType ?? ""}`;
};

export const BondSection = () => {
	const formMethods = useFormContext<AdminCombinedFormData>();
	const { control, watch, setValue, getValues } = formMethods;
	const isMinimalUIEnabled = useIsMinimalBondUIEnabled();
	const isCommercialSuretyEnabled = useIsCommercialSuretyEnabled();
	const bondType = watch("bondType");
	const contractBondType = watch("contractBondType");
	const contractSuretyType = watch("quote.contractSuretyType");
	const prevBondTypeRef = useRef(
		quoteResetKey(isCommercialSuretyEnabled, bondType, contractBondType, contractSuretyType),
	);
	const filesController = useController({ control, name: "quote.files" });
	const resolvedContractSuretyType = isCommercialSuretyEnabled
		? resolveContractSuretyType(bondType, contractBondType)
		: contractSuretyType;
	const isCommercialBond = isCommercialSuretyEnabled && isCommercialSuretyBondType(bondType);
	const showContractBondType = isCommercialSuretyEnabled && isContractCategoryBondType(bondType);
	const bondTypeOptions = useMemo(
		() =>
			isCommercialSuretyEnabled ? buildCommercialBondTypeOptions(true) : LEGACY_BOND_TYPE_OPTIONS,
		[isCommercialSuretyEnabled],
	);

	useEffect(() => {
		if (!isCommercialSuretyEnabled) return;
		const resolved = resolveContractSuretyType(bondType, contractBondType);
		if (resolved && resolved !== getValues("quote.contractSuretyType")) {
			setValue("quote.contractSuretyType", resolved);
		}
	}, [bondType, contractBondType, getValues, isCommercialSuretyEnabled, setValue]);

	useEffect(() => {
		const current = quoteResetKey(
			isCommercialSuretyEnabled,
			bondType,
			contractBondType,
			contractSuretyType,
		);
		if (prevBondTypeRef.current !== undefined && current !== prevBondTypeRef.current) {
			for (const field of quoteFieldsToClear) {
				setValue(field, undefined);
			}
			if (isCommercialSuretyBondType(bondType)) {
				setValue("contractBondType", undefined);
			} else {
				setValue("commercialBond.bondAmount", undefined);
				setValue("commercialBond.effectiveDate", undefined);
				setValue("commercialBond.bondForm", EMPTY_BOND_FORM);
			}
		}
		prevBondTypeRef.current = current;
	}, [bondType, contractBondType, contractSuretyType, isCommercialSuretyEnabled, setValue]);

	return (
		<>
			<div className="grid grid-cols-[1fr_1fr_1fr] gap-4">
				<QuickSubmitFormItemGroup data-testid="bond-type">
					<FormDropdownComponent
						formMethods={formMethods}
						name={isCommercialSuretyEnabled ? "bondType" : "quote.contractSuretyType"}
						label="Bond Type"
						placeholder="Select One"
						options={bondTypeOptions}
						anchor="bottom start"
						compact
					/>
				</QuickSubmitFormItemGroup>
				{showContractBondType && (
					<QuickSubmitFormItemGroup data-testid="contract-bond-type">
						<FormDropdownComponent
							formMethods={formMethods}
							name="contractBondType"
							label="Contract Bond Type"
							placeholder="Select bond type"
							options={ContractBondSubtype.options()}
							anchor="bottom start"
							compact
						/>
					</QuickSubmitFormItemGroup>
				)}
				{!isMinimalUIEnabled && !isCommercialBond && <BondTypeSpecificFields />}
			</div>
			{isCommercialBond && <CommercialBondFields />}
			{resolvedContractSuretyType !== undefined && isMinimalUIEnabled && !isCommercialBond && (
				<>
					<div className="flex flex-col gap-[13px]">
						<FormInputComponent
							formMethods={formMethods}
							name={"quote.project.name"}
							label="Project Name"
							tooltip={PROJECT_NAME_TOOLTIP}
						/>
					</div>
					{resolvedContractSuretyType === "bid" && (
						<div className="flex flex-col gap-[13px]" data-testid="bid-amount">
							<FormCurrencyComponent
								formMethods={formMethods}
								name={"quote.bidAmount"}
								label="Bid Amount"
								compact
							/>
						</div>
					)}
					<div className="flex flex-col gap-[13px]" data-testid="bond-file-upload">
						<FormItemLabel>Upload</FormItemLabel>
						<FileUpload
							showTabs={false}
							value={filesController.field.value ?? []}
							onChange={filesController.field.onChange}
							onBlur={filesController.field.onBlur}
							enablePDFViewer={false}
							allowDelete
							enableTypeSelection={false}
							allowedTypesAndLabels={
								resolvedContractSuretyType === "bid"
									? { [FileType.bid_package]: "RFP / Bid Package" as const }
									: { [FileType.contract]: "Contract" as const }
							}
							maxTypesPerFile={1}
							hideChecklist
							analyticsContext="submission-upload"
							trackingPrefix="account-admin-quick-bond-upload"
						/>
					</div>
				</>
			)}
			{resolvedContractSuretyType !== undefined && !isMinimalUIEnabled && !isCommercialBond && (
				<div className="flex flex-col gap-[13px]">
					<FormInputComponent
						formMethods={formMethods}
						name={"quote.project.description"}
						label="Project Description"
						testId="project-description-input"
					/>
				</div>
			)}
		</>
	);
};

const CommercialBondFields = () => {
	const formMethods = useFormContext<AdminCombinedFormData>();
	const { control } = formMethods;
	const bondForm = formMethods.watch("commercialBond.bondForm");
	const bondFormSelected = (bondForm?.upload?.files.length ?? 0) > 0;

	return (
		<div className="flex flex-col gap-4" data-testid="commercial-bond-fields">
			<div className="grid grid-cols-[1fr_1fr_1fr] gap-4">
				<QuickSubmitFormItemGroup data-testid="commercial-bond-amount">
					<FormCurrencyComponent
						formMethods={formMethods}
						name="commercialBond.bondAmount"
						label="Bond Amount"
						compact
					/>
				</QuickSubmitFormItemGroup>
				<QuickSubmitFormItemGroup data-testid="commercial-effective-date">
					<FormDateComponent
						formMethods={formMethods}
						name="commercialBond.effectiveDate"
						label="Effective Date"
						compact
					/>
				</QuickSubmitFormItemGroup>
			</div>
			<div className="flex flex-col gap-[13px]" data-testid="commercial-bond-form">
				<BondFormSectionHeader
					tooltipId="account-quick-submit-commercial-bond-form"
					showTooltip={!bondFormSelected}
					tooltipContent={BOND_FORM_UPLOAD_ONLY_TOOLTIP}
					label={<FormItemLabel>{BOND_FORM_HEADER}</FormItemLabel>}
					className="gap-1.5"
					tooltipClassName="text-[16px]"
					tooltipMaxWidth={300}
					condensed={false}
				/>
				<FormBondFormSelect<AdminCombinedFormData>
					name="commercialBond.bondForm"
					control={control}
					templates={EMPTY_TEMPLATES}
					typeLabels={NO_TYPE_LABELS}
					allowLibrary={false}
					id="commercialBondForm"
					compact
				/>
			</div>
		</div>
	);
};

const useSyncBondToContract = () => {
	const formMethods = useFormContext<AdminCombinedFormData>();
	const contractAmountTouched = useRef(false);
	const bondType = formMethods.watch("quote.contractSuretyType");

	useEffect(() => {
		contractAmountTouched.current = false;
	}, [bondType]);

	const onBondAmountBlur = () => {
		if (contractAmountTouched.current) return;
		const bondAmount = formMethods.getValues("quote.bondAmount");
		if (bondAmount) {
			formMethods.setValue("quote.contractAmount", bondAmount);
		}
	};

	const onContractAmountChange = () => {
		contractAmountTouched.current = true;
	};

	return { onBondAmountBlur, onContractAmountChange };
};

const BondTypeSpecificFields = () => {
	const formMethods = useFormContext<AdminCombinedFormData>();
	const { watch } = formMethods;
	const bondType = watch("quote.contractSuretyType");
	const { onBondAmountBlur, onContractAmountChange } = useSyncBondToContract();

	if (!bondType) return null;

	if (bondType === "bid") {
		return (
			<>
				<QuickSubmitFormItemGroup data-testid="bid-amount">
					<FormCurrencyComponent
						formMethods={formMethods}
						name={"quote.bidAmount"}
						label="Bid Amount"
						compact
					/>
				</QuickSubmitFormItemGroup>
				<QuickSubmitFormItemGroup data-testid="bid-bond-amount">
					<FormCurrencyPercentComponent
						currencyName={"quote.bondAmount"}
						percentName={"quote.bondAmountPercentOfBid"}
						amountTypeName={"quote.bidBondAmountType"}
						formMethods={formMethods}
						referenceSourceName={"quote.bidAmount"}
						defaultAmountType="percent"
						compact
					/>
				</QuickSubmitFormItemGroup>
			</>
		);
	}

	return (
		<>
			<QuickSubmitFormItemGroup data-testid="bond-amount">
				<FormCurrencyComponent
					formMethods={formMethods}
					name={"quote.bondAmount"}
					label="Bond Amount"
					compact
					onValueBlur={onBondAmountBlur}
				/>
			</QuickSubmitFormItemGroup>
			<QuickSubmitFormItemGroup data-testid="contract-amount">
				<FormCurrencyComponent
					formMethods={formMethods}
					name={"quote.contractAmount"}
					label="Contract Amount"
					compact
					onValueChange={onContractAmountChange}
				/>
			</QuickSubmitFormItemGroup>
		</>
	);
};
